import React, { useCallback, useContext, useEffect, useState } from "react";
import useDeviceLocation from "../hooks/useDeviceLocation";
import { DEFAULT_DEVICE_LOCATION, GeoLocationByPlace } from "../types/GeoLocation";
import { getAirQualityIndex, getWeather, getWeatherAlertsByCity } from "../services/weatherAndLocationService";
import { CurrentWeather, DailyWeather, DailyWeatherUnit, DegreeType, HourlyAirQualityIndex, HourlyWeather, HourlyWeatherUnit, Weather, WeatherAlert } from "../types/Weather";
import { getDailyList, getHourlyList, getCurrentAirQualityIndex, getHourlyWeatherUnit, getDailyWeatherUnit } from "../helpers/weather-helpers";
import { getDegreeTypeInStorage, getLastSearchedLocationInStorage, setLastSearchedLocationInStorage } from "../services/localStorageService";

interface LocationWeatherContextData {
  isLoading: boolean,
  deviceLocationErrMsg: string | null,
  geocodingBaseUrl: string | undefined,
  setCurrentSearchLocation: (location: GeoLocationByPlace) => void,
  weather: Weather | null,
  hourlyWeatherMap: Map<string, HourlyWeather[]>,
  hourlyWeatherUnit: HourlyWeatherUnit | undefined,
  dailyWeatherMap: Map<string, DailyWeather>,
  dailyWeatherUnit: DailyWeatherUnit | undefined,
  currentWeather: CurrentWeather | undefined,
  setDegreeType: (degreeType: DegreeType) => void,
  weatherAlerts: WeatherAlert[],
  currentAirQualityIndex: HourlyAirQualityIndex | undefined,
  lastSearchedLocation: GeoLocationByPlace | undefined,
};

const GEOCODING_BASE_URL = process.env.REACT_APP_GEOCODING_BASE_URL;
const WEATHER_BASE_URL = process.env.REACT_APP_WEATHER_BASE_URL;
const AQI_BASE_URL = process.env.REACT_APP_AQI_BASE_URL;
const IPAPI_BASE_URL = process.env.REACT_APP_IPAPI_BASE_URL;
const WEATHER_ALERTS_URL = process.env.REACT_APP_WEATHER_ALERTS_URL;

export const LocationWeatherContext = React.createContext<LocationWeatherContextData>({
  isLoading: false,
  deviceLocationErrMsg: null,
  geocodingBaseUrl: GEOCODING_BASE_URL,
  setCurrentSearchLocation: (location: GeoLocationByPlace) => { },
  weather: null,
  hourlyWeatherMap: new Map<string, HourlyWeather[]>(),
  hourlyWeatherUnit: undefined,
  dailyWeatherMap: new Map<string, DailyWeather>(),
  dailyWeatherUnit: undefined,
  currentWeather: undefined,
  setDegreeType: () => { },
  weatherAlerts: [],
  currentAirQualityIndex: undefined,
  lastSearchedLocation: DEFAULT_DEVICE_LOCATION,
});

const LocationWeatherProvider = ({ children }: { children: JSX.Element }) => {
  const [lastSearchedLocation, setLastSearchedLocation] = useState<GeoLocationByPlace | undefined>();
  const [weather, setWeather] = useState<Weather | null>(null);
  const [hourlyWeatherMap, setHourlyWeatherMap] = useState<Map<string, HourlyWeather[]>>(new Map());
  const [hourlyWeatherUnit, setHourlyWeatherUnit] = useState<HourlyWeatherUnit>();
  const [dailyWeatherMap, setDailyWeatherMap] = useState<Map<string, DailyWeather>>(new Map());
  const [dailyWeatherUnit, setDailyWeatherUnit] = useState<DailyWeatherUnit>();
  const [currentWeather, setCurrentWeather] = useState<CurrentWeather>();
  const [degreeType, setDegreeType] = useState<DegreeType>('celsius');
  const [isPending, setIsPending] = useState(false);
  const [weatherAlerts, setWeatherAlerts] = useState<WeatherAlert[]>([]);
  const [currentAirQualityIndex, setCurrentAirQualityIndex] = useState<HourlyAirQualityIndex>();

  const setCurrentSearchLocation = useCallback((currentSearchedLocation: GeoLocationByPlace) => {
    setLastSearchedLocationInStorage(currentSearchedLocation);
    setLastSearchedLocation(currentSearchedLocation);
  }, []);

  useEffect(() => {
    // We get the last searched location from local storage
    const lastSearchedLocationInStorage = getLastSearchedLocationInStorage();
    setLastSearchedLocation(lastSearchedLocationInStorage);
  }, [lastSearchedLocation?.id]);

  const { lat: deviceLat, long: deviceLong, city, country, errMsg } = useDeviceLocation(IPAPI_BASE_URL!);
  const weatherLat = !lastSearchedLocation ? deviceLat : (lastSearchedLocation.isDeviceLocation ? deviceLat : lastSearchedLocation.latitude);
  const weatherLong = !lastSearchedLocation ? deviceLong : (lastSearchedLocation!.isDeviceLocation ? deviceLong : lastSearchedLocation.longitude);
  const weatherCity = !lastSearchedLocation ? city : (lastSearchedLocation.isDeviceLocation ? city : lastSearchedLocation.name);
  const weatherCountry = !lastSearchedLocation ? country : (lastSearchedLocation.isDeviceLocation ? country : lastSearchedLocation!.country);

  // Get Weather Alerts by City
  useEffect(() => {
    if (!weatherCity) {
      return;
    }

    setIsPending(true);
    getWeatherAlertsByCity(weatherCity, weatherCountry ?? "", WEATHER_ALERTS_URL!)
      .then((weatherAlerts) => {
        setWeatherAlerts(weatherAlerts);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsPending(false);
      });
  }, [weatherCity, weatherCountry]);

  // Get Air Quality Index
  useEffect(() => {
    setIsPending(true);
    getAirQualityIndex(weatherLat, weatherLong, AQI_BASE_URL)
      .then((aqi) => {
        const currentAqi = getCurrentAirQualityIndex(aqi);
        setCurrentAirQualityIndex(currentAqi);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsPending(false);
      });

  }, [weatherLat, weatherLong]);

  // Get Weather 
  useEffect(() => {
    setIsPending(true);
    getWeather(
      weatherLat,
      weatherLong,
      getDegreeTypeInStorage(),
      WEATHER_BASE_URL
    )
      .then((weather) => {
        setWeather(weather);
        const hourlyWeather = getHourlyList(weather);
        const hourlyWeatherUnit = getHourlyWeatherUnit(weather);
        const dailyWeather = getDailyList(weather);
        const dailyWeatherUnit = getDailyWeatherUnit(weather);
        setHourlyWeatherMap(hourlyWeather);
        setHourlyWeatherUnit(hourlyWeatherUnit);
        setDailyWeatherMap(dailyWeather);
        setDailyWeatherUnit(dailyWeatherUnit);
        setCurrentWeather(weather?.current_weather);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsPending(false);
      });
  }, [weatherLat, weatherLong, degreeType]);

  return (
    <LocationWeatherContext.Provider value={{
      isLoading: isPending,
      deviceLocationErrMsg: errMsg,
      geocodingBaseUrl: GEOCODING_BASE_URL,
      setCurrentSearchLocation,
      weather,
      hourlyWeatherMap,
      hourlyWeatherUnit,
      dailyWeatherMap,
      dailyWeatherUnit,
      currentWeather,
      setDegreeType,
      weatherAlerts,
      currentAirQualityIndex,
      lastSearchedLocation,
    }}>
      {children}
    </LocationWeatherContext.Provider>

  );
}

export default LocationWeatherProvider;
export const useLocationWeatherContext = () => useContext(LocationWeatherContext);